"use client";

import { Check, ChevronDown, ExternalLink } from "lucide-react";
import { Popover } from "../ui/Popover";
import {
  parseTaskReportOrigin,
  TASK_REPORT_CHANNELS,
  type TaskReportChannel,
} from "../../lib/taskReportOrigin";
import { ReportChannelIcon } from "./ReportChannelIcon";

const CHANNEL_LABELS: Record<TaskReportChannel, string> = {
  pauta: "Pauta",
  slack: "Slack",
  codex: "Codex",
  claude_code: "Claude Code",
  github: "GitHub",
  api: "API",
  other: "Other",
};

export function ReportChannelValue({
  channel,
  context,
  url,
}: {
  channel?: TaskReportChannel | null;
  context?: string | null;
  url?: string | null;
}) {
  const label = channel ? CHANNEL_LABELS[channel] ?? "Unknown" : "Unknown";
  const origin = parseTaskReportOrigin({
    report_channel: channel ?? null,
    report_context: context ?? null,
    report_url: url ?? null,
  });
  const href = origin.ok ? origin.value.report_url : null;
  const content = (
    <>
      <ReportChannelIcon channel={channel} />
      <span className="min-w-0 break-words">
        {label}{channel && context ? <span> · {context}</span> : null}
      </span>
      {href ? <ExternalLink size={12} className="shrink-0" aria-hidden="true" /> : null}
    </>
  );
  const className = "inline-flex max-w-full items-start gap-1.5 rounded-md px-1.5 py-1 text-[12px] text-[var(--fg-secondary)]";

  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      referrerPolicy="no-referrer"
      aria-label={`Open report source: ${label}${context ? ` · ${context}` : ""} (opens in a new tab)`}
      className={`${className} hover:bg-[var(--bg-hover)] transition-colors`}
    >
      {content}
    </a>
  ) : (
    <div className={className}>{content}</div>
  );
}

export function ReportChannelPicker({
  value,
  onChange,
}: {
  value: TaskReportChannel;
  onChange: (channel: TaskReportChannel) => void;
}) {
  return (
    <Popover
      trigger={
        <button
          type="button"
          aria-label={`Canal do reporte: ${CHANNEL_LABELS[value]}`}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-2 py-1 text-[12px] text-[var(--fg-secondary)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)] transition-colors"
        >
          <ReportChannelIcon channel={value} />
          <span>{CHANNEL_LABELS[value]}</span>
          <ChevronDown size={12} aria-hidden="true" />
        </button>
      }
    >
      {(close) => (
        <div className="py-1" role="group" aria-label="Canal do reporte">
          {TASK_REPORT_CHANNELS.map((channel) => (
            <button
              key={channel}
              type="button"
              aria-pressed={value === channel}
              onClick={() => {
                onChange(channel);
                close();
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] transition-colors"
            >
              <ReportChannelIcon channel={channel} />
              <span>{CHANNEL_LABELS[channel]}</span>
              {value === channel ? <Check size={13} className="ml-auto" aria-hidden="true" /> : null}
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}
